// src/services/api.js
import { entityDetailQueries, updateQueries } from '../repository/graphQueries';
import { API_BASE_URL, ACCOUNT_SERVICE_URL, STAFF_EVAL_LAMBDA_URL } from '../config';

// Simple in-memory cache with TTL
class ApiCache {
  constructor(defaultTTL = 300000) { // 5 minutes default for better navigation experience
    this.cache = new Map();
    this.defaultTTL = defaultTTL;
  }

  generateKey(endpoint, options = {}) {
    // Create a cache key from endpoint URL (which includes query params)
    // The URL already contains all the filter parameters
    return endpoint;
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    // Check if cache entry is still valid
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  set(key, data, ttl = null) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + (ttl || this.defaultTTL)
    });
  }

  clear() {
    this.cache.clear();
  }

  invalidate(pattern) {
    // Invalidate cache entries matching a pattern
    if (!pattern) {
      this.clear();
      return;
    }
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
}

// Create axios-like interceptor for fetch
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.accountServiceURL = ACCOUNT_SERVICE_URL;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.cache = new ApiCache(); // Use default 5 minute TTL for cache
  }

  // Add request interceptor
  addRequestInterceptor(onFulfilled, onRejected) {
    this.requestInterceptors.push({ onFulfilled, onRejected });
  }

  // Add response interceptor
  addResponseInterceptor(onFulfilled, onRejected) {
    this.responseInterceptors.push({ onFulfilled, onRejected });
  }

  // Execute request interceptors
  async executeRequestInterceptors(config) {
    let modifiedConfig = config;
    for (const interceptor of this.requestInterceptors) {
      try {
        modifiedConfig = await interceptor.onFulfilled(modifiedConfig);
      } catch (error) {
        if (interceptor.onRejected) {
          await interceptor.onRejected(error);
        }
        throw error;
      }
    }
    return modifiedConfig;
  }

  // Execute response interceptors
  async executeResponseInterceptors(response) {
    let modifiedResponse = response;
    for (const interceptor of this.responseInterceptors) {
      try {
        modifiedResponse = await interceptor.onFulfilled(modifiedResponse);
      } catch (error) {
        if (interceptor.onRejected) {
          await interceptor.onRejected(error);
        }
        throw error;
      }
    }
    return modifiedResponse;
  }

  // Helper function to format dates properly
  formatDateParam(date) {
    if (!date) return undefined;

    // If it's already a string, return it
    if (typeof date === 'string') return date;

    // If it's a Date object, format as YYYY-MM-DD
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    // If it's a moment object
    if (date && typeof date.format === 'function') {
      return date.format('YYYY-MM-DD');
    }

    // If it's an object with date properties, try to convert
    if (typeof date === 'object' && date !== null) {
      // Handle dayjs or similar objects
      if (date.$d instanceof Date) {
        return date.$d.toISOString().split('T')[0];
      }
      // Handle plain objects with year, month, day
      if (date.year && date.month && date.day) {
        return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
      }
    }

    console.warn('Unable to format date:', date);
    return undefined;
  }

  async request(endpoint, options = {}) {
    try {
      // Automatically prepend /api to endpoints unless they already have it or are special routes
      let finalEndpoint = endpoint;
      if (!endpoint.startsWith('/api') && !endpoint.startsWith('/auth') && !endpoint.startsWith('/health')) {
        finalEndpoint = `/api${endpoint}`;
      }
      
      // Prepare request config
      const config = {
        url: `${this.baseURL}${finalEndpoint}`,
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
      };

      // Execute request interceptors
      const modifiedConfig = await this.executeRequestInterceptors(config);

      // Make the request
      const response = await fetch(modifiedConfig.url, {
        method: modifiedConfig.method,
        headers: modifiedConfig.headers,
        body: modifiedConfig.body,
        credentials: 'include', // Include cookies for cross-origin requests
      });

      // Execute response interceptors
      const modifiedResponse = await this.executeResponseInterceptors(response);

      if (!modifiedResponse.ok) {
        const errorText = await modifiedResponse.text();
        console.error(`HTTP error ${modifiedResponse.status}:`, errorText);
        throw new Error(`HTTP error! status: ${modifiedResponse.status}`);
      }

      const contentType = modifiedResponse.headers.get('content-type');
      if (modifiedResponse.status === 204 || !contentType || !contentType.includes('application/json')) {
        return; // No content or not JSON, return undefined
      }

      return modifiedResponse.json();
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  }

  get(endpoint, options = {}) {
    // Check cache for GET requests
    const cacheKey = endpoint; // Use full endpoint URL as cache key
    const cached = this.cache.get(cacheKey);
    
    if (cached) {
      return Promise.resolve(cached);
    }
    
    // If not cached, make the request and cache the result
    return this.request(endpoint, { ...options, method: 'GET' })
      .then(data => {
        // Cache successful GET requests with default TTL
        this.cache.set(cacheKey, data);
        return data;
      });
  }

  // Check if data exists in cache without fetching
  hasCache(endpoint) {
    const cached = this.cache.get(endpoint);
    return !!cached;
  }

  post(endpoint, data, options = {}) {
    // Invalidate cache for mutations
    const pattern = endpoint.split('?')[0]; // Get base endpoint without query params
    this.cache.invalidate(pattern);
    
    return this.request(endpoint, { 
      ...options, 
      method: 'POST', 
      body: data 
    });
  }

  put(endpoint, data, options = {}) {
    // Invalidate cache for mutations
    const pattern = endpoint.split('?')[0];
    this.cache.invalidate(pattern);
    
    return this.request(endpoint, { 
      ...options, 
      method: 'PUT', 
      body: data 
    });
  }

  delete(endpoint, options = {}) {
    // Invalidate cache for mutations
    const pattern = endpoint.split('?')[0];
    this.cache.invalidate(pattern);
    
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  patch(endpoint, data, options = {}) {
    // Invalidate cache for mutations
    const pattern = endpoint.split('?')[0];
    this.cache.invalidate(pattern);
    
    return this.request(endpoint, { 
      ...options, 
      method: 'PATCH', 
      body: data 
    });
  }

  // ===== CLIENT METHODS =====
  async getAllClientsWithStats() {
    return this.get('/clients');
  }

  async getClientList() {
    return this.get('/clients/list');
  }

  async getClientById(clientId) {
    return this.get(`/clients/${clientId}`);
  }

  async createClient(clientData) {
    this.cache.invalidate('/clients');
    return this.post('/clients', clientData);
  }

  async updateClient(clientId, updates) {
    this.cache.invalidate('/clients');
    return this.patch(`/clients/${clientId}`, updates);
  }

  async deleteClient(clientId) {
    this.cache.invalidate('/clients');
    return this.delete(`/clients/${clientId}`);
  }

  async getClientOverview(clientId, driFilter = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.get(`/clients/${clientId}/overview?${params.toString()}`);
  }

  async getClientCommunicationFrequency(clientId) {
    return this.get(`/clients/${clientId}/communication-frequency`);
  }

  async getClientHealthScore(clientId) {
    return this.get(`/clients/${clientId}/health-score`);
  }

  async getClientHealthTrend(clientId, days = 90) {
    return this.get(`/clients/${clientId}/health-trend?days=${days}`);
  }

  async getClientEvaluationDetails(clientId, evaluationId = null, observationName = null) {
    let params = [];
    if (evaluationId) params.push(`evaluationId=${evaluationId}`);
    if (observationName) params.push(`observationName=${observationName}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.get(`/clients/${clientId}/evaluation-details${queryString}`);
  }

  async getFrameworkComponents(clientId) {
    return this.get(`/clients/${clientId}/framework-components`);
  }

  async getComponentDetails(clientId, componentId) {
    return this.get(`/clients/${clientId}/framework-components/${componentId}/details`);
  }

  async getClientActionItems(clientId, driFilter = null, initiativeFilter = null, startDate = null, endDate = null, statusFilter = null) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    // Handle both string and array for initiativeFilter
    if (initiativeFilter) {
      if (Array.isArray(initiativeFilter)) {
        initiativeFilter.forEach(init => params.append('initiativeFilter', init));
      } else if (initiativeFilter.length > 0) {
        params.append('initiativeFilter', initiativeFilter);
      }
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (statusFilter) params.append('statusFilter', statusFilter);
    return this.get(`/action-items/${validClientId}/pending-list?${params.toString()}`);
  }

  async getActionItemsByAssignee(clientId) {
    const validClientId = this.ensureClientId(clientId);
    return this.get(`/action-items/${validClientId}/by-assignee`);
  }

  async getClientIssues(clientId, driFilter = null, severityFilter = null, statusFilter = null, startDate = null, endDate = null) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (severityFilter) params.append('severityFilter', severityFilter);
    if (statusFilter) params.append('statusFilter', statusFilter);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return this.get(`/issues/${validClientId}/all?${params.toString()}`);
  }

  async getClientInitiatives(clientId, driFilter = null, statusFilter = null, startDate = null, endDate = null) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (statusFilter) params.append('statusFilter', statusFilter);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.get(`/initiatives/${validClientId}/all?${params.toString()}`);
  }

  async getClientDistinctDRIs(clientId) {
    return this.get(`/clients/${clientId}/dris`);
  }

  async getClientDistinctInitiatives(clientId) {
    return this.get(`/initiatives/${clientId}/distinct`);
  }

  async getClientGoals(clientId, driFilter = null, statusFilter = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (statusFilter) params.append('statusFilter', statusFilter);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.get(`/goals/${clientId}/list?${params.toString()}`);
  }

  // Helper method to get a default client ID when user doesn't have one
  getDefaultClientId() {
    // For now, return 'CoDev' as the default client ID
    // In a real application, this should be configurable or fetched from user preferences
    return 'CoDev';
  }

  // Helper method to ensure we have a valid client ID
  ensureClientId(clientId) {
    return clientId || this.getDefaultClientId();
  }

  // ===== DEPARTMENT METHODS =====
  async getDepartmentList() {
    return this.get('/departments');
  }

  async getDepartmentDRIs(departmentId) {
    return this.get(`/departments/${departmentId}/dris`);
  }

  async getDepartmentActionItems(departmentId, params = {}) {
    const { startDate, endDate, assigneeFilter, initiativeFilter, driFilter } = params;
    const searchParams = new URLSearchParams({ startDate, endDate });
    if (assigneeFilter) searchParams.append('assigneeFilter', assigneeFilter);
    if (initiativeFilter) searchParams.append('initiativeFilter', initiativeFilter);
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => searchParams.append('driFilter', dri));
    }
    return this.get(`/departments/${departmentId}/action-items?${searchParams.toString()}`);
  }

  async getDepartmentGoals(departmentId, params = {}) {
    const { startDate, endDate, driFilter } = params;
    const searchParams = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => searchParams.append('driFilter', dri));
    }
    return this.get(`/departments/${departmentId}/goals?${searchParams.toString()}`);
  }

  async getDepartmentInitiatives(departmentId, params = {}) {
    const { startDate, endDate, driFilter } = params;
    const searchParams = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => searchParams.append('driFilter', dri));
    }
    return this.get(`/departments/${departmentId}/initiatives?${searchParams.toString()}`);
  }

  async getDepartmentIssues(departmentId, params = {}) {
    const { startDate, endDate, severityFilter, statusFilter, driFilter } = params;
    const searchParams = new URLSearchParams();
    const formattedStartDate = this.formatDateParam(startDate);
    if (formattedStartDate) {
      searchParams.append('startDate', formattedStartDate);
    }
    const formattedEndDate = this.formatDateParam(endDate);
    if (formattedEndDate) {
      searchParams.append('endDate', formattedEndDate);
    }
    // Note: backend expects 'severity', 'status', 'dri' (not 'severityFilter', etc.)
    if (severityFilter) {
      searchParams.append('severity', severityFilter);
    }
    if (statusFilter) {
      searchParams.append('status', statusFilter);
    }
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(d => searchParams.append('dri', d));
    }
    return this.request(`/departments/${departmentId}/issues?${searchParams.toString()}`);
  }

  async getDepartmentIssuesList(departmentId, startDate, endDate, severityFilter, statusFilter, driFilter, clientFilter = null) {
    const params = new URLSearchParams();
    const formattedStartDate = this.formatDateParam(startDate);
    if (formattedStartDate) {
      params.append('startDate', formattedStartDate);
    }
    const formattedEndDate = this.formatDateParam(endDate);
    if (formattedEndDate) {
      params.append('endDate', formattedEndDate);
    }
    // Note: backend expects 'severity', 'status', 'dri' (not 'severityFilter', etc.)
    if (severityFilter) {
      params.append('severity', severityFilter);
    }
    if (statusFilter) {
      params.append('status', statusFilter);
    }
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(d => params.append('dri', d));
    }
    if (clientFilter && clientFilter.length > 0) {
      clientFilter.forEach(client => params.append('clientFilter', client));
    }
    return this.request(`/departments/${departmentId}/issues?${params.toString()}`);
  }

  async getDepartmentPriorityCounts(departmentId, startDate, endDate, driFilter = null) {
    const params = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    return this.get(`/departments/${departmentId}/priority-counts?${params.toString()}`);
  }

  async getDepartmentGoalsList(departmentId, startDate, endDate, driFilter = null, statusFilter = null, clientFilter = null) {
    const params = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (statusFilter) params.append('statusFilter', statusFilter);
    if (clientFilter && clientFilter.length > 0) {
      clientFilter.forEach(client => params.append('clientFilter', client));
    }
    return this.get(`/departments/${departmentId}/goals?${params.toString()}`);
  }

  async getDepartmentInitiativesList(departmentId, startDate, endDate, driFilter = null, statusFilter = null, clientFilter = null) {
    const params = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (statusFilter) params.append('statusFilter', statusFilter);
    if (clientFilter && clientFilter.length > 0) {
      clientFilter.forEach(client => params.append('clientFilter', client));
    }
    return this.get(`/departments/${departmentId}/initiatives?${params.toString()}`);
  }

  async getDepartmentActionItemsList(departmentId, startDate, endDate, driFilter = null, initiativeFilter = null, clientFilter = null, assigneeFilter = null) {
    const params = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    // Handle both string and array for initiativeFilter
    if (initiativeFilter) {
      if (Array.isArray(initiativeFilter)) {
        initiativeFilter.forEach(init => params.append('initiativeFilter', init));
      } else if (initiativeFilter.length > 0) {
        params.append('initiativeFilter', initiativeFilter);
      }
    }
    if (clientFilter && clientFilter.length > 0) {
      clientFilter.forEach(client => params.append('clientFilter', client));
    }
    if (assigneeFilter) {
      params.append('assigneeFilter', assigneeFilter);
    }
    return this.get(`/departments/${departmentId}/action-items?${params.toString()}`);
  }

  async createDepartmentActionItem(departmentId, actionItemData) {
    // Invalidate department action-item-related caches
    this.cache.invalidate('/departments');
    this.cache.invalidate('/action-items');
    return this.post(`/departments/${departmentId}/action-items`, actionItemData);
  }

  async createDepartmentGoal(departmentId, goalData) {
    // Invalidate department goal-related caches
    this.cache.invalidate('/departments');
    this.cache.invalidate('/goals');
    return this.post(`/departments/${departmentId}/goals`, goalData);
  }

  async createDepartmentInitiative(departmentId, initiativeData) {
    // Invalidate department initiative-related caches
    this.cache.invalidate('/departments');
    this.cache.invalidate('/initiatives');
    return this.post(`/departments/${departmentId}/initiatives`, initiativeData);
  }

  async createDepartmentIssue(departmentId, issueData) {
    // Invalidate department issue-related caches
    this.cache.invalidate('/departments');
    this.cache.invalidate('/issues');
    return this.post(`/departments/${departmentId}/issues`, issueData);
  }

  async getDepartmentRelatedClients(departmentId, startDate, endDate) {
    const params = new URLSearchParams({ startDate, endDate });
    return this.get(`/departments/${departmentId}/related-clients?${params.toString()}`);
  }

  async getDepartmentPriorityCommunications(departmentId, startDate, endDate) {
    const params = new URLSearchParams({ startDate, endDate });
    return this.get(`/departments/${departmentId}/priority-communications?${params.toString()}`);
  }

  async getDepartmentCommunications(departmentId, startDate, endDate, driFilter = null) {
    const params = new URLSearchParams({ startDate, endDate });
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    return this.get(`/departments/${departmentId}/communications?${params.toString()}`);
  }

  async getDepartmentStaffWorkload(departmentId) {
    return this.get(`/departments/${departmentId}/staff-workload`);
  }

  async getDepartmentClientHealth(departmentId, startDate, endDate) {
    const params = new URLSearchParams({ startDate, endDate });
    return this.get(`/departments/${departmentId}/client-health?${params.toString()}`);
  }

  async getDepartmentSentimentOverTime(departmentId, startDate, endDate) {
    const params = new URLSearchParams({ startDate, endDate });
    return this.get(`/departments/${departmentId}/sentiment-over-time?${params.toString()}`);
  }

  // ===== STAFF METHODS =====
  async getAllStaff(includeInactive = false) {
    return this.request(`/staff/list?includeInactive=${includeInactive}`);
  }

  async getStaffProfile(staffId) {
    return this.get(`/staff/${staffId}/profile`);
  }

  async createStaff(staffData) {
    this.cache.invalidate('/staff');
    return this.post('/staff', staffData);
  }

  async updateStaff(staffId, updates) {
    this.cache.invalidate('/staff');
    return this.patch(`/staff/${staffId}`, updates);
  }

  async toggleStaffActive(staffId, active = null) {
    this.cache.invalidate('/staff');
    const body = active !== null ? { active } : {};
    return this.patch(`/staff/${staffId}/active`, body);
  }

  async getStaffDetails(staffId, clientId = null) {
    const params = new URLSearchParams();
    if (clientId) params.append('clientId', clientId);
    return this.request(`/staff/${staffId}/details?${params.toString()}`);
  }

  async getCommunicatedClientsForStaff(staffId) {
    return this.request(`/staff/${staffId}/clients`);
  }

  async getStaffEvaluationReport(staffId, clientId = null) {
    const params = new URLSearchParams();
    if (clientId) params.append('clientId', clientId);
    return this.request(`/staff/${staffId}/evaluation-report?${params.toString()}`);
  }

  // Method to get all data for the staff view in one call
  async getStaffViewData(staffId, clientId = null) {
    const params = new URLSearchParams();
    if (clientId) {
      params.append('clientId', clientId);
    }
    return this.request(`/staff/${staffId}/view-data?${params.toString()}`);
  }

  async getStaffPerformanceSummaryList() {
    return this.request('/staff/performance-summary');
  }

  async triggerStaffEvaluation(staffId, staffName, startDate, endDate, idToken) {
    // Call the Lambda function URL with JWT authentication (async version)
    const lambdaUrl = STAFF_EVAL_LAMBDA_URL;
    
    try {
      if (!idToken) {
        throw new Error('ID token is required for staff evaluation');
      }
      
      // Detect environment based on current URL
      const currentUrl = window.location.hostname;
      let environment = 'production'; // default
      if (currentUrl.includes('staging')) {
        environment = 'staging';
      }
      
      // Call Lambda function with ID token and environment info
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-Environment': environment // Add environment header
        },
      body: JSON.stringify({
        staff_id: staffId,
        staff_name: staffName,
        start_date: startDate,
        end_date: endDate,
        environment: environment // Also include in body for redundancy
      })
      });
      
      // Check if response is ok first
      if (!response.ok) {
        let errorMessage;
        try {
          // Try to read as text first, then parse as JSON if possible
          const responseText = await response.text();
          try {
            const errorResult = JSON.parse(responseText);
            errorMessage = errorResult.message || 'Unknown error';
          } catch (parseError) {
            // Not valid JSON, use the text directly
            errorMessage = responseText || `Server error (${response.status})`;
          }
        } catch (readError) {
          errorMessage = `Server error (${response.status})`;
        }
        throw new Error(`Failed to trigger evaluation: ${errorMessage}`);
      }
      
      const result = await response.json();
      
      // Handle both sync (200) and async (202) responses
      if (response.status === 202) {
        // Async processing - request accepted
        return {
          ...result,
          isAsync: true,
          status: 'accepted'
        };
      } else if (response.status === 200) {
        // Sync processing - completed immediately
        return {
          ...result,
          isAsync: false,
          status: 'completed'
        };
      }
      
      return result;
    } catch (error) {
      console.error('Error triggering staff evaluation:', error);
      throw error;
    }
  }

  async getStaffEvaluationStatus(requestId) {
    // Check evaluation status (could be implemented later for async tracking)
    return this.request(`/staff/evaluation-status/${requestId}`);
  }

  // ===== GOAL METHODS =====
  async getAllGoalsList(clientId, driFilter = null, statusFilter = null, startDate = null, endDate = null) {
    return this.getClientGoals(clientId, driFilter, statusFilter, startDate, endDate);
  }

  async getGoalById(clientId, goalId) {
    return this.get(`/goals/${clientId}/${goalId}`);
  }

  async createGoal(clientId, goalData) {
    // Invalidate goal-related caches
    this.cache.invalidate('/goals');
    this.cache.invalidate('/clients');
    return this.post(`/goals/${clientId}`, goalData);
  }

  async updateGoal(clientId, goalId, updates) {
    // Invalidate goal-related caches
    this.cache.invalidate('/goals');
    this.cache.invalidate('/clients');
    return this.patch(`/goals/${clientId}/${goalId}`, updates);
  }

  async deleteGoal(clientId, goalId) {
    // Invalidate goal-related caches
    this.cache.invalidate('/goals');
    this.cache.invalidate('/clients');
    return this.delete(`/goals/${clientId}/${goalId}`);
  }

  // ===== INITIATIVE METHODS =====
  async getAllInitiativesList(clientId, driFilter = null, statusFilter = null, startDate = null, endDate = null) {
    return this.getClientInitiatives(clientId, driFilter, statusFilter, startDate, endDate);
  }

  async getInitiativeById(clientId, initiativeId) {
    return this.get(`/initiatives/${clientId}/${initiativeId}`);
  }

  async createInitiative(clientId, initiativeData) {
    // Invalidate initiative-related caches
    this.cache.invalidate('/initiatives');
    this.cache.invalidate('/clients');
    return this.post(`/initiatives/${clientId}`, initiativeData);
  }

  async updateInitiative(clientId, initiativeId, updates) {
    // Invalidate initiative-related caches
    this.cache.invalidate('/initiatives');
    this.cache.invalidate('/clients');
    return this.patch(`/initiatives/${clientId}/${initiativeId}`, updates);
  }

  async deleteInitiative(clientId, initiativeId) {
    // Invalidate initiative-related caches
    this.cache.invalidate('/initiatives');
    this.cache.invalidate('/clients');
    return this.delete(`/initiatives/${clientId}/${initiativeId}`);
  }

  // ===== ISSUE METHODS =====
  async getAllIssuesList(clientId, driFilter = null, severityFilter = null, statusFilter = null, startDate = null, endDate = null) {
    return this.getClientIssues(clientId, driFilter, severityFilter, statusFilter, startDate, endDate);
  }

  async getIssueById(clientId, issueId) {
    return this.get(`/issues/${clientId}/${issueId}`);
  }

  async createIssue(clientId, issueData) {
    // Invalidate issue-related caches
    this.cache.invalidate('/issues');
    this.cache.invalidate('/clients');
    return this.post(`/issues/${clientId}`, issueData);
  }

  async updateIssue(clientId, issueId, updates) {
    // Invalidate issue-related caches
    this.cache.invalidate('/issues');
    this.cache.invalidate('/clients');
    return this.patch(`/issues/${clientId}/${issueId}`, updates);
  }

  async deleteIssue(clientId, issueId) {
    // Invalidate issue-related caches
    this.cache.invalidate('/issues');
    this.cache.invalidate('/clients');
    return this.delete(`/issues/${clientId}/${issueId}`);
  }

  // ===== ACTION ITEM METHODS =====
  async getAllPendingActionItemsList(clientId, driFilter = null, initiativeFilter = null, startDate = null, endDate = null, statusFilter = null) {
    return this.getClientActionItems(clientId, driFilter, initiativeFilter, startDate, endDate, statusFilter);
  }

  async getActionItemById(clientId, actionItemId) {
    return this.get(`/action-items/${clientId}/${actionItemId}`);
  }

  async createActionItem(clientId, actionItemData) {
    // Invalidate action-item-related caches
    this.cache.invalidate('/action-items');
    this.cache.invalidate('/clients');
    return this.post(`/action-items/${clientId}`, actionItemData);
  }

  async updateActionItem(clientId, actionItemId, updates) {
    // Invalidate action-item-related caches
    this.cache.invalidate('/action-items');
    this.cache.invalidate('/clients');
    return this.patch(`/action-items/${clientId}/${actionItemId}`, updates);
  }

  async deleteActionItem(clientId, actionItemId) {
    // Invalidate action-item-related caches
    this.cache.invalidate('/action-items');
    this.cache.invalidate('/clients');
    return this.delete(`/action-items/${clientId}/${actionItemId}`);
  }

  async linkActionItemToInitiative(clientId, actionItemId, initiativeId) {
    this.cache.invalidate('/action-items');
    return this.post(`/action-items/${clientId}/${actionItemId}/link-initiative/${initiativeId}`, {});
  }

  async unlinkActionItemFromInitiative(clientId, actionItemId, initiativeId) {
    this.cache.invalidate('/action-items');
    return this.delete(`/action-items/${clientId}/${actionItemId}/link-initiative/${initiativeId}`);
  }

  // ===== COMMUNICATION METHODS =====
  async getAllCommunicationsList(clientId, startDate, endDate) {
    return this.getClientCommunications(clientId, startDate, endDate);
  }

  // ===== NETWORK METHODS =====
  async getClientNetworkData(clientId, filters = {}) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.departmentId) params.append('departmentId', filters.departmentId);
    return this.request(`/network/${validClientId}/data?${params.toString()}`);
  }

  // ===== EISENHOWER MATRIX METHODS =====
  async getEisenhowerData(clientId, filters = {}) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (filters.departmentId) {
      params.append('departmentId', filters.departmentId);
    }
    const formattedStartDate = this.formatDateParam(filters.startDate);
    if (formattedStartDate) {
      params.append('startDate', formattedStartDate);
    }
    const formattedEndDate = this.formatDateParam(filters.endDate);
    if (formattedEndDate) {
      params.append('endDate', formattedEndDate);
    }
    if (filters.driFilter && filters.driFilter.length > 0) {
      filters.driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (filters.typeFilter && filters.typeFilter.length > 0) {
      filters.typeFilter.forEach(type => params.append('typeFilter', type));
    }
    if (filters.clientFilter && filters.clientFilter.length > 0) {
      filters.clientFilter.forEach(client => params.append('clientFilter', client));
    }
    return this.request(`/eisenhower-matrix/${validClientId}?${params.toString()}`);
  }

  // ===== OVERVIEW METHODS =====
  async getPriorityCounts(clientId, driFilter = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.get(`/overview/${clientId}/priority-counts?${params.toString()}`);
  }

  async getGoalsAtRisk(clientId, driFilter = null, startDate = null, endDate = null) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.get(`/goals/${validClientId}/at-risk?${params.toString()}`);
  }

  async getIssuesByRiskScore(clientId, driFilter = null, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.get(`/overview/${clientId}/issues-by-risk?${params.toString()}`);
  }

  async getCommunicationFrequencyByType(clientId, driFilter = null, startDate = null, endDate = null, granularity = 'week') {
    const params = new URLSearchParams();
    if (driFilter && driFilter.length > 0) {
      driFilter.forEach(dri => params.append('driFilter', dri));
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (granularity) params.append('granularity', granularity);
    return this.get(`/overview/${clientId}/communication-frequency?${params.toString()}`);
  }

  // ===== SENTIMENT METHODS =====
  async getSentimentOverTime(clientId, startDate, endDate) {
    return this.getClientSentimentTrend(clientId, startDate, endDate);
  }

  async getClientSentimentTrend(clientId, startDate, endDate) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/sentiment/${validClientId}/sentiment-over-time?${params.toString()}`);
  }

  async getClientCommunications(clientId, startDate, endDate) {
    const validClientId = this.ensureClientId(clientId);
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/sentiment/${validClientId}/communications?${params.toString()}`);
  }

  // ===== ENTITY DETAIL METHODS =====
  async getEntityDetails(entityType, clientId, entityId) {
    return this.request(`/entity-detail/${clientId}/${entityType}/${entityId}`);
  }

  async getEntityMentionTrend(clientId, entityId) {
    return this.request(`/entity-detail/${clientId}/entities/${entityId}/mention-trend`);
  }

  async getEntitySentimentTrend(clientId, entityId) {
    return this.request(`/entity-detail/${clientId}/entities/${entityId}/sentiment-trend`);
  }

  async getEntityLinkedComms(clientId, entityId) {
    return this.request(`/entity-detail/${clientId}/entities/${entityId}/linked-comms`);
  }

  // Goal relationship methods
  async getGoalSupportingInitiatives(clientId, goalId) {
    return this.request(`/entity-detail/${clientId}/goals/${goalId}/supporting-initiatives`);
  }

  async getGoalBlockingIssues(clientId, goalId) {
    return this.request(`/entity-detail/${clientId}/goals/${goalId}/blocking-issues`);
  }

  // Initiative relationship methods
  async getInitiativeBlockingIssues(clientId, initiativeId) {
    return this.request(`/entity-detail/${clientId}/initiatives/${initiativeId}/blocking-issues`);
  }

  // Issue relationship methods
  async getIssueBlockedInitiatives(clientId, issueId) {
    return this.request(`/entity-detail/${clientId}/issues/${issueId}/blocked-initiatives`);
  }

  // ActionItem relationship methods
  async getActionItemSupportedInitiative(clientId, actionItemId) {
    return this.request(`/entity-detail/${clientId}/action-items/${actionItemId}/supported-initiative`);
  }

  // ===== UPDATE METHODS =====
  async updateNodeProperty(nodeId, propertyName, newValue) {
    // Invalidate caches that might contain this node
    this.cache.invalidate('/issues');
    this.cache.invalidate('/goals');
    this.cache.invalidate('/initiatives');
    this.cache.invalidate('/action-items');
    this.cache.invalidate('/departments');
    
    return this.request(`/updates/node/${nodeId}/property`, {
      method: 'PUT',
      body: { propertyName, newValue }
    });
  }

  async updateNodeStatus(nodeId, newValue) {
    // Invalidate caches that might contain this node
    // This covers issues, goals, initiatives, action-items lists
    this.cache.invalidate('/issues');
    this.cache.invalidate('/goals');
    this.cache.invalidate('/initiatives');
    this.cache.invalidate('/action-items');
    this.cache.invalidate('/departments');
    
    return this.request(`/updates/node/${nodeId}/status`, {
      method: 'PUT',
      body: { newValue }
    });
  }

  async updateIssueSeverity(issueId, newValue) {
    // Invalidate issue-related caches
    this.cache.invalidate('/issues');
    this.cache.invalidate('/departments');
    
    return this.request(`/updates/issue/${issueId}/severity`, {
      method: 'PUT',
      body: { newValue }
    });
  }

  async updateEntityDRI(nodeId, assignedTo) {
    this.cache.invalidate('/issues');
    this.cache.invalidate('/goals');
    this.cache.invalidate('/initiatives');
    this.cache.invalidate('/action-items');
    this.cache.invalidate('/departments');
    
    return this.request(`/updates/node/${nodeId}/dri`, {
      method: 'PUT',
      body: { assignedTo }
    });
  }

  // ===== AUDIT METHODS =====
  async getChangeHistory(nodeId) {
    return this.request(`/audit/history/${nodeId}`);
  }

  async revertChange(changeLogId) {
    return this.request(`/audit/revert/${changeLogId}`, {
      method: 'PUT',
      body: {}
    });
  }

  // ===== INSIGHT METHODS =====
  async getInsightDecliningTrend(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/declining-trend?${params.toString()}`);
  }

  async getInsightLongBlockedInitiatives(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/long-blocked-initiatives?${params.toString()}`);
  }

  async getInsightStaffFocusDistribution(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/staff-focus-distribution?${params.toString()}`);
  }

  async getInsightUnaddressedInitiatives(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/unaddressed-initiatives?${params.toString()}`);
  }

  async getInsightApproachingDeadlines(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/approaching-deadlines?${params.toString()}`);
  }

  async getInsightOverdueActionItems(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/overdue-action-items?${params.toString()}`);
  }

  async getInsightGoalsAtRisk(clientId, startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/insights/${clientId}/goals-at-risk?${params.toString()}`);
  }

  // ===== LEGACY COMPATIBILITY METHODS =====
  
  // Helper method to extract query key from query string
  getQueryKey(query) {
    // Look for the query key in the entityDetailQueries object
    for (const [key, value] of Object.entries(entityDetailQueries)) {
      if (value === query) {
        return key;
      }
    }
    
    // Also check updateQueries
    for (const [key, value] of Object.entries(updateQueries)) {
      if (value === query) {
        return key;
      }
    }
    
    return null;
  }
  
  // This method provides backward compatibility for components still using runQuery
  async runQuery(query, parameters = {}) {
    console.log(`Legacy runQuery called with query:`, query.substring(0, 100) + '...');
    
    // Extract parameters
    const { clientId, departmentId, startDate, endDate, driFilter, initiativeFilter, severityFilter, statusFilter, entityId } = parameters;

    // Map known query patterns to new API endpoints
    if (query.includes('listAllClients') || query.includes('getAllClients')) {
      return this.getAllClientsWithStats();
    }
    
    if (query.includes('getDistinctDRIsForClient')) {
      return this.getClientDistinctDRIs(clientId);
    }
    
    if (query.includes('getAllGoalsList')) {
      return this.getAllGoalsList(clientId, driFilter);
    }
    
    if (query.includes('getAllInitiativesList')) {
      return this.getAllInitiativesList(clientId, driFilter);
    }
    
    if (query.includes('getAllIssuesList')) {
      return this.getAllIssuesList(clientId, driFilter, severityFilter, statusFilter);
    }
    
    if (query.includes('getAllPendingActionItemsList')) {
      return this.getAllPendingActionItemsList(clientId, driFilter, initiativeFilter);
    }
    
    if (query.includes('listAllDepartments')) {
      return this.getDepartmentList();
    }
    
    if (query.includes('getDepartmentDRIs')) {
      return this.getDepartmentDRIs(departmentId);
    }
    
    if (query.includes('getDepartmentGoalsList')) {
      return this.getDepartmentGoalsList(departmentId, startDate, endDate, driFilter);
    }
    
    if (query.includes('getDepartmentInitiativesList')) {
      return this.getDepartmentInitiativesList(departmentId, startDate, endDate, driFilter);
    }
    
    if (query.includes('getDepartmentActionItemsList')) {
      return this.getDepartmentActionItemsList(departmentId, startDate, endDate, driFilter, initiativeFilter);
    }
    
    if (query.includes('getDepartmentIssuesList')) {
      return this.getDepartmentIssuesList(departmentId, startDate, endDate, severityFilter, statusFilter, driFilter);
    }
    
    if (query.includes('getAllStaff')) {
      return this.getAllStaff();
    }
    
    if (query.includes('getSentimentOverTime')) {
      return this.getSentimentOverTime(clientId, startDate, endDate);
    }
    
    if (query.includes('getAllCommunicationsList')) {
      return this.getAllCommunicationsList(clientId, startDate, endDate);
    }

    // ===== ENTITY DETAIL QUERY MAPPINGS =====
    
    // Map exact query keys to specific API endpoints
    const queryKey = this.getQueryKey(query);
    
    // Entity detail queries
    if (queryKey === 'getGoalDetails') {
      return this.getEntityDetails('goals', clientId, entityId);
    }
    if (queryKey === 'getInitiativeDetails') {
      return this.getEntityDetails('initiatives', clientId, entityId);
    }
    if (queryKey === 'getIssueDetails') {
      return this.getEntityDetails('issues', clientId, entityId);
    }
    if (queryKey === 'getActionItemDetails') {
      return this.getEntityDetails('action-items', clientId, entityId);
    }
    
    // Entity trend queries
    if (queryKey === 'getGoalMentionTrend' || queryKey === 'getInitiativeMentionTrend' || 
        queryKey === 'getIssueMentionTrend' || queryKey === 'getActionItemMentionTrend') {
      return this.getEntityMentionTrend(clientId, entityId);
    }
    
    if (queryKey === 'getGoalSentimentTrend' || queryKey === 'getInitiativeSentimentTrend' || 
        queryKey === 'getIssueSentimentTrend' || queryKey === 'getActionItemSentimentTrend') {
      return this.getEntitySentimentTrend(clientId, entityId);
    }
    
    // Entity communication queries
    if (queryKey === 'getGoalLinkedComms' || queryKey === 'getInitiativeLinkedComms' || 
        queryKey === 'getIssueLinkedComms' || queryKey === 'getActionItemLinkedComms') {
      return this.getEntityLinkedComms(clientId, entityId);
    }
    
    // Entity relationship queries
    if (queryKey === 'getGoalSupportingInitiatives') {
      return this.getGoalSupportingInitiatives(clientId, entityId);
    }
    if (queryKey === 'getGoalBlockingIssues') {
      return this.getGoalBlockingIssues(clientId, entityId);
    }
    if (queryKey === 'getInitiativeBlockingIssues') {
      return this.getInitiativeBlockingIssues(clientId, entityId);
    }
    if (queryKey === 'getIssueBlockedInitiatives') {
      return this.getIssueBlockedInitiatives(clientId, entityId);
    }
    if (queryKey === 'getActionItemSupportedInitiative') {
      return this.getActionItemSupportedInitiative(clientId, entityId);
    }

    // ===== UPDATE QUERY MAPPINGS =====
    const { nodeId, newValue, propertyName } = parameters;
    
    if (queryKey === 'updateNodeStatus') {
      return this.updateNodeStatus(nodeId, newValue);
    }
    
    if (queryKey === 'updateNodeSeverity') {
      return this.updateIssueSeverity(nodeId, newValue);
    }
    
    if (queryKey === 'updateNodeProperty') {
      return this.updateNodeProperty(nodeId, propertyName, newValue);
    }

    console.error(`Legacy query not mapped to any API endpoint:`, query.substring(0, 100));
    console.error(`Full query:`, query);
    console.error(`Parameters:`, parameters);
    throw new Error(`Legacy query is not mapped to a specific API endpoint. Please update to use the new API methods.`);
  }

  // ===== UTILITY METHODS =====
  async initialize() {
    try {
      // Use a simple health check endpoint if available, otherwise use an existing one.
      await this.request('/clients/list');
      console.log('Backend service is reachable');
      return true;
    } catch (error) {
      console.error('Backend service not reachable:', error);
      return false;
    }
  }

  close() {
    // No-op for API service
  }

  // Method to clear cache (useful for debugging or manual refresh)
  clearCache(pattern) {
    if (pattern) {
      this.cache.invalidate(pattern);
    } else {
      this.cache.clear();
      console.debug('🗑️ All cache cleared');
    }
  }

  // Method to get cache stats
  getCacheStats() {
    return {
      size: this.cache.cache.size,
      keys: Array.from(this.cache.cache.keys())
    };
  }
}

// Create API service instance
const apiService = new ApiService();

// Add request interceptor to handle authentication via JWT token
apiService.addRequestInterceptor(async (config) => {
  try {
    // Authentication bypass for development has been removed to prevent security vulnerabilities.
    
    // Try to get JWT token from account-service
    try {
      const response = await fetch(`${apiService.accountServiceURL}/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          // Add JWT token to Authorization header
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${data.token}`;
        }
      }
    } catch (error) {
      console.warn('Could not retrieve JWT token:', error);
      // Continue without token - let the backend handle the 401
    }
    
  } catch (error) {
    console.error('Error in request interceptor:', error);
    // Don't throw here, let the request proceed without token
  }
  return config;
});

// Add response interceptor to handle 401 responses
apiService.addResponseInterceptor(async (response) => {
  if (response.status === 401) {
    try {
      console.log('Received 401 response - user needs to re-authenticate');
      // Redirect to login page
      window.location.href = '/';
    } catch (error) {
      console.error('Error handling 401 response:', error);
    }
  }
  return response;
});

// Legacy function for backward compatibility
export const callAPI = async (endpoint, options = {}) => {
  return apiService.request(endpoint, options);
};

// Export the API service instance
export default apiService;
